<script>
/* global axios */
import { useVuelidate } from '@vuelidate/core';
import { required } from '@vuelidate/validators';
import { useAlert } from 'dashboard/composables';
import { loadScript } from 'dashboard/helper/DOMHelpers';
import router from '../../../../index';
import NextButton from 'dashboard/components-next/button/Button.vue';
import MetricaBillingAPI from 'dashboard/api/metrica/billing';

const CHAKRA_STATUS = Object.freeze({
  PENDING: 'pending',
  CONNECTED: 'connected',
  ERROR: 'error',
});

const POLL_INTERVAL_MS = 4000;

// Confirmed live (2026-07-06, via Chakra's own API docs, signed in): this is
// Chakra's official embeddable widget for the partner connect flow — an
// iframe hosted on their own domain (so it isn't subject to the Facebook App
// domain whitelist, which blocks calling FB.login() with their app_id
// directly from ours — that approach was tried first and doesn't work).
// See metrica/docs/CHAKRA-INTEGRATION.md.
const CHAKRA_EMBED_SDK_URL =
  'https://embed.chakrahq.com/whatsapp-partner-connect/v1_0_1/sdk.js';

export default {
  components: { NextButton },

  setup() {
    // CHAKRA_STATUS must be exposed here so the template (Options API) can
    // access it — module-level constants aren't part of the component proxy.
    return { v$: useVuelidate(), CHAKRA_STATUS };
  },

  data() {
    return {
      step: 'form', // 'form' | 'connecting'
      inboxName: '',
      createdInboxId: null,

      connectionStatus: CHAKRA_STATUS.PENDING,
      connectError: null,
      pollTimer: null,
      isLoadingWidget: false,
      chakraWidget: null,

      isLoadingSupport: true,
      support: {},
      loadError: false,
    };
  },

  validations: {
    inboxName: { required },
  },

  computed: {
    uiFlags() {
      return this.$store.getters['inboxes/getUIFlags'];
    },

    accountId() {
      return this.$route.params.accountId;
    },

    // Chakra credentials (CHAKRA_API_TOKEN) are a server-level ENV var, same
    // as WAHA_BASE_URL/WAHA_API_KEY — until they're set, fall back to the
    // original "talk to our team" contact CTA instead of a broken form.
    isChakraConfigured() {
      return window.chatwootConfig?.chakraConfigured === 'true';
    },

    isConnectingStep() {
      return this.step === 'connecting';
    },

    isConnected() {
      return this.connectionStatus === CHAKRA_STATUS.CONNECTED;
    },

    isError() {
      return this.connectionStatus === CHAKRA_STATUS.ERROR;
    },

    hasContact() {
      return Boolean(
        this.support.whatsapp || this.support.email || this.support.url
      );
    },

    contactHref() {
      if (this.support.whatsapp) {
        return `https://wa.me/${this.support.whatsapp}`;
      }
      if (this.support.url) {
        return this.support.url;
      }
      if (this.support.email) {
        return `mailto:${this.support.email}`;
      }
      return '#';
    },
  },

  async mounted() {
    if (this.isChakraConfigured) return;

    try {
      const { data } = await MetricaBillingAPI.status();
      this.support = data?.support || {};
    } catch (error) {
      this.loadError = true;
    } finally {
      this.isLoadingSupport = false;
    }
  },

  beforeUnmount() {
    this.stopPolling();
    this.chakraWidget?.destroy();
  },

  methods: {
    // ── Step 1: create the pending inbox ──────────────────────────────────
    async createChannel() {
      this.v$.$touch();
      if (this.v$.$invalid) return;

      try {
        const inbox = await this.$store.dispatch('inboxes/createChannel', {
          name: this.inboxName.trim(),
          channel: {
            type: 'whatsapp',
            provider: 'chakra',
            provider_config: {},
          },
        });

        this.createdInboxId = inbox.id;
        this.step = 'connecting';
        await this.mountChakraWidget();
      } catch (error) {
        const isLimitReached = String(error.message || '').includes(
          'chakra_number_limit_reached'
        );
        useAlert(
          isLimitReached
            ? this.$t(
                'INBOX_MGMT.ADD.WHATSAPP.OFFICIAL_PARTNER.CONNECT.NUMBER_LIMIT_REACHED'
              )
            : error.message ||
                this.$t(
                  'INBOX_MGMT.ADD.WHATSAPP.OFFICIAL_PARTNER.CONNECT.CREATE_ERROR'
                )
        );
      }
    },

    // ── Step 2: mint a connect token and mount Chakra's embeddable connect
    // widget (an iframe on their own domain — see CHAKRA_EMBED_SDK_URL
    // above). Its onSuccess callback hands back the plugin_id directly, so
    // no guessing/polling is needed to discover it.
    async mountChakraWidget() {
      // Guards against a double-click re-entering this method, and against
      // retrying from the error state without first switching the template
      // back to the branch that renders the `chakraContainer` div — without
      // this, `this.$refs.chakraContainer` would still be undefined below.
      if (this.isLoadingWidget) return;
      this.connectionStatus = CHAKRA_STATUS.PENDING;
      this.connectError = null;
      this.isLoadingWidget = true;
      try {
        const { data } = await axios.post(
          `/api/v1/accounts/${this.accountId}/inboxes/${this.createdInboxId}/chakra_connect`
        );

        await loadScript(CHAKRA_EMBED_SDK_URL, { async: true });
        // A retry mounts a fresh widget with a fresh connect token — drop
        // the previous iframe instance first, then wait a tick so the
        // container element (rendered by the `step === 'connecting'`
        // branch) actually exists in the DOM before we mount into it.
        this.chakraWidget?.destroy();
        this.$nextTick(() => {
          this.chakraWidget = window.ChakraWhatsappConnect.init({
            connectToken: data.connect_token,
            container: this.$refs.chakraContainer,
            style: { width: '100%' },
            onError: error => {
              this.connectionStatus = CHAKRA_STATUS.ERROR;
              this.connectError = error?.message || String(error);
            },
            onSuccess: result => this.onWidgetSuccess(result),
          });
          this.isLoadingWidget = false;
        });
      } catch (error) {
        this.connectionStatus = CHAKRA_STATUS.ERROR;
        this.connectError = error?.response?.data?.error || error.message;
        this.isLoadingWidget = false;
      }
    },

    // Chakra's widget already resolved the client's WABA/phone — we only
    // need to record which plugin_id it belongs to. The rest (real phone
    // number, webhook registration, template sync) is handled by the
    // existing chakra_status poll below, unchanged.
    async onWidgetSuccess(result) {
      try {
        await axios.post(
          `/api/v1/accounts/${this.accountId}/inboxes/${this.createdInboxId}/chakra_link`,
          { plugin_id: result?.pluginId }
        );
        this.startPolling();
      } catch (error) {
        this.connectionStatus = CHAKRA_STATUS.ERROR;
        this.connectError = error?.response?.data?.error || error.message;
      }
    },

    startPolling() {
      this.pollStatus();
      this.pollTimer = setInterval(this.pollStatus, POLL_INTERVAL_MS);
    },

    stopPolling() {
      clearInterval(this.pollTimer);
    },

    async pollStatus() {
      try {
        const { data } = await axios.get(
          `/api/v1/accounts/${this.accountId}/inboxes/${this.createdInboxId}/chakra_status`
        );
        this.connectionStatus = data.status || CHAKRA_STATUS.PENDING;
        this.connectError = data.error || null;

        if (this.connectionStatus === CHAKRA_STATUS.CONNECTED) {
          this.stopPolling();
          this.onConnected();
        }
      } catch {
        // Network error — keep polling
      }
    },

    onConnected() {
      router.replace({
        name: 'settings_inboxes_add_agents',
        params: { page: 'new', inbox_id: this.createdInboxId },
      });
    },
  },
};
</script>

<template>
  <div class="flex flex-col gap-6">
    <!-- Chakra credentials configured: real self-service connect flow -->
    <template v-if="isChakraConfigured">
      <p class="text-sm leading-relaxed text-n-slate-11">
        {{ $t('INBOX_MGMT.ADD.WHATSAPP.OFFICIAL_PARTNER.CONNECT.DESCRIPTION') }}
      </p>

      <div class="p-4 rounded-xl border border-n-weak bg-n-alpha-2">
        <h3 class="mb-2 text-sm font-medium text-n-slate-12">
          {{ $t('INBOX_MGMT.ADD.WHATSAPP.OFFICIAL_PARTNER.ABOUT.TITLE') }}
        </h3>
        <ul class="space-y-1.5 text-sm list-disc list-inside text-n-slate-11">
          <li>
            {{ $t('INBOX_MGMT.ADD.WHATSAPP.OFFICIAL_PARTNER.ABOUT.OFFICIAL') }}
          </li>
          <li>
            {{
              $t(
                'INBOX_MGMT.ADD.WHATSAPP.OFFICIAL_PARTNER.ABOUT.INCLUDED_NUMBER'
              )
            }}
          </li>
          <li>
            {{ $t('INBOX_MGMT.ADD.WHATSAPP.OFFICIAL_PARTNER.ABOUT.META_STEP') }}
          </li>
          <li>
            {{ $t('INBOX_MGMT.ADD.WHATSAPP.OFFICIAL_PARTNER.ABOUT.DATA') }}
          </li>
          <li>
            {{
              $t('INBOX_MGMT.ADD.WHATSAPP.OFFICIAL_PARTNER.ABOUT.SETUP_TIME')
            }}
          </li>
        </ul>
      </div>

      <!-- Step 1: inbox name -->
      <form
        v-if="!isConnectingStep"
        class="flex flex-col"
        @submit.prevent="createChannel()"
      >
        <label :class="{ error: v$.inboxName.$error }">
          {{
            $t(
              'INBOX_MGMT.ADD.WHATSAPP.OFFICIAL_PARTNER.CONNECT.INBOX_NAME.LABEL'
            )
          }}
          <input
            v-model="inboxName"
            type="text"
            :placeholder="
              $t(
                'INBOX_MGMT.ADD.WHATSAPP.OFFICIAL_PARTNER.CONNECT.INBOX_NAME.PLACEHOLDER'
              )
            "
            @blur="v$.inboxName.$touch"
          />
          <span v-if="v$.inboxName.$error" class="message">
            {{
              $t(
                'INBOX_MGMT.ADD.WHATSAPP.OFFICIAL_PARTNER.CONNECT.INBOX_NAME.ERROR'
              )
            }}
          </span>
        </label>

        <div class="w-full mt-4">
          <NextButton
            :is-loading="uiFlags.isCreating"
            type="submit"
            solid
            blue
            :label="
              $t(
                'INBOX_MGMT.ADD.WHATSAPP.OFFICIAL_PARTNER.CONNECT.SUBMIT_BUTTON'
              )
            "
          />
        </div>
      </form>

      <!-- Step 2: connecting / status -->
      <div v-else class="flex flex-col items-center gap-4 py-4 text-center">
        <template v-if="isConnected">
          <span class="i-ri-checkbox-circle-fill text-3xl text-success-500" />
          <p class="text-sm text-n-slate-11">
            {{
              $t(
                'INBOX_MGMT.ADD.WHATSAPP.OFFICIAL_PARTNER.CONNECT.STATUS.CONNECTED'
              )
            }}
          </p>
        </template>
        <template v-else-if="isError">
          <span class="i-ri-error-warning-line text-3xl text-ruby-500" />
          <p class="text-xs text-ruby-600">
            {{
              connectError ||
              $t(
                'INBOX_MGMT.ADD.WHATSAPP.OFFICIAL_PARTNER.CONNECT.STATUS.ERROR'
              )
            }}
          </p>
          <NextButton
            :is-loading="isLoadingWidget"
            solid
            blue
            :label="
              $t(
                'INBOX_MGMT.ADD.WHATSAPP.OFFICIAL_PARTNER.CONNECT.RETRY_BUTTON'
              )
            "
            @click="mountChakraWidget"
          />
        </template>
        <template v-else>
          <span
            v-if="isLoadingWidget"
            class="i-ri-loader-4-line animate-spin text-3xl text-n-brand"
          />
          <p class="text-sm text-n-slate-11">
            {{
              $t(
                'INBOX_MGMT.ADD.WHATSAPP.OFFICIAL_PARTNER.CONNECT.STATUS.PENDING'
              )
            }}
          </p>
          <div ref="chakraContainer" class="w-full flex justify-center" />
        </template>
      </div>
    </template>

    <!-- Chakra not configured yet on this server: static contact fallback -->
    <template v-else>
      <p class="text-sm leading-relaxed text-n-slate-11">
        {{ $t('INBOX_MGMT.ADD.WHATSAPP.OFFICIAL_PARTNER.DESCRIPTION') }}
      </p>

      <div class="p-4 rounded-xl border border-n-weak bg-n-alpha-2">
        <h3 class="mb-2 text-sm font-medium text-n-slate-12">
          {{ $t('INBOX_MGMT.ADD.WHATSAPP.OFFICIAL_PARTNER.ABOUT.TITLE') }}
        </h3>
        <ul class="space-y-1.5 text-sm list-disc list-inside text-n-slate-11">
          <li>
            {{ $t('INBOX_MGMT.ADD.WHATSAPP.OFFICIAL_PARTNER.ABOUT.OFFICIAL') }}
          </li>
          <li>
            {{
              $t(
                'INBOX_MGMT.ADD.WHATSAPP.OFFICIAL_PARTNER.ABOUT.INCLUDED_NUMBER'
              )
            }}
          </li>
          <li>
            {{ $t('INBOX_MGMT.ADD.WHATSAPP.OFFICIAL_PARTNER.ABOUT.META_STEP') }}
          </li>
          <li>
            {{ $t('INBOX_MGMT.ADD.WHATSAPP.OFFICIAL_PARTNER.ABOUT.DATA') }}
          </li>
          <li>
            {{
              $t(
                'INBOX_MGMT.ADD.WHATSAPP.OFFICIAL_PARTNER.ABOUT.ASSISTED_SETUP'
              )
            }}
          </li>
        </ul>
      </div>

      <p v-if="isLoadingSupport" class="text-sm text-n-slate-11">
        {{ $t('INBOX_MGMT.ADD.WHATSAPP.OFFICIAL_PARTNER.CONTACT.LOADING') }}
      </p>
      <p v-else-if="loadError || !hasContact" class="text-sm text-n-slate-11">
        {{ $t('INBOX_MGMT.ADD.WHATSAPP.OFFICIAL_PARTNER.CONTACT.UNAVAILABLE') }}
      </p>
      <p v-else class="text-sm text-n-slate-11">
        {{ $t('INBOX_MGMT.ADD.WHATSAPP.OFFICIAL_PARTNER.CONTACT.PREFIX') }}
        <a
          :href="contactHref"
          target="_blank"
          rel="noopener noreferrer"
          class="font-medium text-n-brand hover:underline"
        >
          {{ $t('INBOX_MGMT.ADD.WHATSAPP.OFFICIAL_PARTNER.CONTACT.CTA') }}
        </a>
      </p>
    </template>
  </div>
</template>
